// Probabilistic attribution of commands ← traces.
//
// Each command is scored against the prompts that plausibly caused it.
// Unlike a binary "since last request" filter, attribution can split a
// command's causal weight across multiple recent prompts using time
// decay so a tool call running long after a prompt can still attribute
// back, with a soft fade as competing prompts arrive.
//
// Inputs
//   traces[]   — { id, timestamp, sessionId, summary }
//   commands[] — { id, timestamp, sessionId, command, tool }
//
// Outputs
//   byCommand: Map<commandId, {
//     links: [{ traceId, probability, deltaMs, factors }],
//     topTraceId,
//     ambiguous,
//   }>
//   byTrace: Map<traceId, {
//     items: [{ commandId, probability, deltaMs }],
//     total,
//     ownedCount,
//   }>

const POST_DECAY_TAU_MS = 60_000;
const PRE_DECAY_TAU_MS = 3_000;
const MAX_POST_DELTA_MS = 5 * 60_000;
const MAX_PRE_DELTA_MS = 10_000;
const PRE_WEIGHT = 0.3;
const CROSS_SESSION_WEIGHT = 0.05;
const CONTENT_BOOST = 2.0;
const MIN_LINK_PROB = 0.05;
const MAX_LINKS_PER_COMMAND = 5;
const AMBIGUITY_THRESHOLD = 0.25;

const STOPWORDS = new Set([
  "the","a","an","and","or","of","to","in","on","for","with","is","are","was",
  "were","be","been","by","at","as","it","this","that","from","but","not","no",
  "what","which","who","why","how","do","does","did","can","could","should",
  "would","i","you","we","they","my","your","our","please","help","me",
]);

function tokenize(text) {
  if (!text) return [];
  return String(text)
    .toLowerCase()
    .split(/[^a-z0-9_\-./]+/)
    .filter((t) => t.length > 1 && !STOPWORDS.has(t));
}

// Set-based Jaccard. Callers precompute the Sets once and reuse them
// across the nested attribution loop — building a new Set per
// (command, trace) pair was the dominant cost in CPU profiles
// (`jaccard` self-time was ~280ms across one local-dev capture).
// Iterates the smaller of the two sets so a 5-token command compared
// against a 50-token trace summary walks 5 elements, not 50.
function jaccardFromSets(aSet, bSet) {
  if (aSet.size === 0 || bSet.size === 0) return 0;
  const [small, big] = aSet.size <= bSet.size ? [aSet, bSet] : [bSet, aSet];
  let inter = 0;
  small.forEach((tok) => { if (big.has(tok)) inter += 1; });
  const union = aSet.size + bSet.size - inter;
  return union > 0 ? inter / union : 0;
}

function decayWeight(deltaMs) {
  if (deltaMs >= 0) {
    if (deltaMs > MAX_POST_DELTA_MS) return 0;
    return Math.exp(-deltaMs / POST_DECAY_TAU_MS);
  }
  const absDelta = -deltaMs;
  if (absDelta > MAX_PRE_DELTA_MS) return 0;
  return PRE_WEIGHT * Math.exp(-absDelta / PRE_DECAY_TAU_MS);
}

function lowerBound(sorted, t) {
  let lo = 0, hi = sorted.length;
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    if (sorted[mid].timestamp < t) lo = mid + 1; else hi = mid;
  }
  return lo;
}

export function computeAttributions(traces, commands, options = {}) {
  const minProb = options.minProbability ?? MIN_LINK_PROB;
  const maxLinks = options.maxLinks ?? MAX_LINKS_PER_COMMAND;

  const byCommand = new Map();
  const byTrace = new Map();

  if (!traces?.length || !commands?.length) {
    return { byCommand, byTrace };
  }

  const sortedTraces = [...traces]
    .filter((t) => Number.isFinite(t?.timestamp) && t.id)
    .sort((a, b) => a.timestamp - b.timestamp)
    .map((t) => {
      const tokens = tokenize(t.summary);
      return {
        id: t.id,
        timestamp: t.timestamp,
        sessionId: t.sessionId || "",
        // Per-trace token Set cached at prep time so the inner
        // attribution loop reads from it instead of rebuilding for
        // every command that lands in this trace's time window.
        tokenSet: new Set(tokens),
      };
    });

  if (!sortedTraces.length) return { byCommand, byTrace };

  for (const cmd of commands) {
    if (!cmd?.id || !Number.isFinite(cmd.timestamp)) continue;

    const cmdSession = cmd.sessionId || "";
    // Precompute the command's token Set once for the whole inner
    // sweep — every (cmd, trace) pair previously rebuilt this Set.
    const cmdTokenSet = new Set(tokenize(cmd.command));

    const lo = lowerBound(sortedTraces, cmd.timestamp - MAX_POST_DELTA_MS);
    const hi = lowerBound(sortedTraces, cmd.timestamp + MAX_PRE_DELTA_MS + 1);

    const scored = [];
    for (let i = lo; i < hi; i++) {
      const t = sortedTraces[i];
      const deltaMs = cmd.timestamp - t.timestamp;
      const timing = decayWeight(deltaMs);
      if (timing <= 0) continue;

      const sessionFactor = cmdSession && t.sessionId === cmdSession
        ? 1
        : CROSS_SESSION_WEIGHT;
      const contentSim = jaccardFromSets(t.tokenSet, cmdTokenSet);
      const contentFactor = 1 + CONTENT_BOOST * contentSim;

      const raw = timing * sessionFactor * contentFactor;
      if (raw <= 0) continue;

      scored.push({
        traceId: t.id,
        deltaMs,
        timing,
        session: sessionFactor,
        content: contentSim,
        raw,
      });
    }

    if (!scored.length) continue;

    const sum = scored.reduce((s, x) => s + x.raw, 0);
    if (sum <= 0) continue;

    scored.forEach((x) => { x.probability = x.raw / sum; });
    scored.sort((a, b) => b.probability - a.probability);

    const kept = scored
      .filter((x) => x.probability >= minProb)
      .slice(0, maxLinks);
    if (!kept.length) continue;

    const topTraceId = kept[0].traceId;
    const second = kept[1]?.probability ?? 0;
    const ambiguous = (kept[0].probability - second) < AMBIGUITY_THRESHOLD;

    const links = kept.map((x) => ({
      traceId: x.traceId,
      probability: x.probability,
      deltaMs: x.deltaMs,
      factors: {
        timing: x.timing,
        session: x.session,
        content: x.content,
      },
    }));

    byCommand.set(cmd.id, { links, topTraceId, ambiguous });

    for (const link of links) {
      let bucket = byTrace.get(link.traceId);
      if (!bucket) {
        bucket = { items: [], total: 0, ownedCount: 0 };
        byTrace.set(link.traceId, bucket);
      }
      bucket.items.push({
        commandId: cmd.id,
        probability: link.probability,
        deltaMs: link.deltaMs,
      });
      bucket.total += 1;
      if (link.traceId === topTraceId) bucket.ownedCount += 1;
    }
  }

  byTrace.forEach((bucket) => {
    bucket.items.sort((a, b) => a.deltaMs - b.deltaMs);
  });

  return { byCommand, byTrace };
}

// Selectable window presets shared by the command-log Context filter.
export const CONTEXT_WINDOW_OPTIONS = Object.freeze([
  { value: 30_000,    label: "30s" },
  { value: 60_000,    label: "1m"  },
  { value: 5 * 60_000,  label: "5m"  },
  { value: 15 * 60_000, label: "15m" },
]);

export const CONTEXT_WINDOW_DEFAULT_MS = 60_000;

// Direction relative to the focal prompt:
//   "before"  — strict pre-window (cmd happened before prompt)
//   "around"  — symmetric (±)
//   "after"   — strict post-window (cmd happened after prompt)
export const CONTEXT_DIRECTION_OPTIONS = Object.freeze([
  { value: "before", glyph: "−", aria: "Before the prompt" },
  { value: "around", glyph: "±", aria: "Around the prompt" },
  { value: "after",  glyph: "+", aria: "After the prompt" },
]);

export const CONTEXT_DIRECTION_DEFAULT = "after";

// Returns true if `deltaMs = cmd.timestamp - focal.timestamp` lies in
// the window defined by (direction, windowMs).
export function deltaInWindow(deltaMs, direction, windowMs) {
  if (direction === "before") return deltaMs <= 0 && -deltaMs <= windowMs;
  if (direction === "after")  return deltaMs >= 0 && deltaMs <= windowMs;
  return Math.abs(deltaMs) <= windowMs; // around
}
